import React from 'react'
import styled from 'styled-components'
import { Link, Image } from 'rebass'
import { Flex, Box } from 'rebass'

import {
  media,
  Small,
  VisuallyHidden,
  MediumScreenUp,
  SmallToMediumScreenOnly
} from '../../utils/style'
import assetLogoCitygate from '../../vectors/logo-citygate-white.svg'
import LogoTwitter from '../../vectors/LogoSocialTwitter'
import LogoFacebook from '../../vectors/LogoSocialFacebook'
import LogoLinkedIn from '../../vectors/LogoSocialLinkedin'
import LogoYoutube from '../../vectors/LogoSocialYoutube'
import LogoInstagram from '../../vectors/LogoSocialInstagram'
import LogoAutoWorks from '../../vectors/LogoAutoWorks'

/*************************
 STYLES: Wrapper
 **************************/
const FullWidthWrapper = styled(Flex)`
  color: ${(props) => props.theme.colors.white};
  background: ${(props) => props.theme.colors.blue};
  border-top: 1px ${(props) => props.theme.colors.white_op10} solid;
  text-align: center;
  ${media.MediumScreenUp`
    text-align: left;
  `};
`

const Container = styled(Box)`
  position: relative;
  max-width: ${(props) => props.theme.content.max.width};
  margin-left: auto;
  margin-right: auto;
  p {
    line-height: 1.5;
    color: ${(props) => props.theme.colors.blue_light};
  }
`

/*************************
 STYLES: Social
 **************************/
const SocialList = styled.ul`
  list-style: none;
  margin: 15px 0 20px;
  align-items: center;
  justify-content: center;
  text-align: center;
  ${media.MediumScreenUp`
    margin: 0;
    align-items: right;
    justify-content: right;
    text-align: right;
    transform: translateX(16px);
  `};

  > li {
    display: inline-block;
    text-align: center;
    transform: scale(1.3);
    ${media.MediumScreenUp`
      transform: scale(1);
    `};

    > a {
      display: block;
      padding-left: 8px;
      padding-right: 8px;
      margin-left: 8px;
      margin-right: 8px;
    }
  }

  svg > path {
    transition: fill 0.5s ${(props) => props.theme.ease.ease_out_expo};
  }
  svg:hover > path {
    fill: ${(props) => props.theme.colors.white_op65};
  }
`

const CitygateLogo = styled(Image)`
  position: absolute;
  bottom: 0;
  left: -10px;
  transform: scale(0.8);
  height: 84px;
  ${media.MediumScreenUp`
    position: static;
    transform: scale(1);
  `};
`

/*************************
 STYLES: SiteWide
 **************************/
const SiteWideLinks = styled(Box)`
  color: white;
  a,
  a:hover {
    color: white;
    font-weight: 600;
    font-size: 12px;
    text-decoration: none;
    transition: color 0.3s ${(props) => props.theme.ease.ease_out_expo};
  }
  a:hover {
    color: ${(props) => props.theme.colors.white_op65};
  }
`

const HiddenLinkLabel = styled.span`
  ${VisuallyHidden}
`

const Border = styled(Flex)`
  border-top: 1px ${(props) => props.theme.colors.white_op10} solid;
`

const AutoWorks = styled(Box)`
  font-size: 12px;
  transform: translateY(-4px);
  a {
    color: white;
  }
  svg {
    transform: translateY(30%);
  }
  svg > path {
    transition: fill 0.3s ${(props) => props.theme.ease.ease_out_expo};
  }
  &:hover svg path,
  &:hover a,
  svg:hover path,
  a:hover + a svg path {
    color: ${(props) => props.theme.colors.white_op65};
    fill: ${(props) => props.theme.colors.white_op65};
  }
`

const SmallImage = styled(Image)`
  height: 64px;
`
const InlineLink = styled(Link)`
  color: #eceff1;
`

const FooterCompany = ({ socialLinks }) => (
  <FullWidthWrapper pt={30} pb={30}>
    <Container width={11 / 12}>
      <Flex flexWrap="wrap">
        <MediumScreenUp>
          <Box width={[2 / 12]}>
            <CitygateLogo src={assetLogoCitygate} alt="Citygate" />
          </Box>
        </MediumScreenUp>
        <Box ml={['auto']} mr={['auto', 0]}>
          <SocialList>
            <li>
              <Link
                target="_blank"
                rel="noopener"
                href={`${process.env.SOCIAL_TWITTER}`}
                ml={0}
              >
                <LogoTwitter alt="Follow us on Twitter" />
                <HiddenLinkLabel>Follow us on Twitter</HiddenLinkLabel>
              </Link>
            </li>
            <li>
              <Link
                target="_blank"
                rel="noopener"
                href={`${socialLinks.facebook || process.env.SOCIAL_FACEBOOK}`}
                ml={4}
              >
                <LogoFacebook alt="Find us on Facebook" />
                <HiddenLinkLabel>Find us on Facebook</HiddenLinkLabel>
              </Link>
            </li>
            <li>
              <Link
                target="_blank"
                rel="noopener"
                href={`${process.env.SOCIAL_LINKEDIN}`}
                ml={4}
              >
                <LogoLinkedIn alt="Connect with us on Linkedin" />
                <HiddenLinkLabel>Connect with us on Linkedin</HiddenLinkLabel>
              </Link>
            </li>
            <li>
              <Link
                target="_blank"
                rel="noopener"
                href={`${
                  socialLinks.instagram || process.env.SOCIAL_INSTAGRAM
                }`}
                ml={4}
              >
                <LogoInstagram alt="Follow us on Instragram" />
                <HiddenLinkLabel>Follow us on Instragram</HiddenLinkLabel>
              </Link>
            </li>
            <li>
              <Link
                target="_blank"
                rel="noopener"
                href={`${process.env.SOCIAL_YOUTUBE}`}
                ml={4}
              >
                <LogoYoutube alt="Watch us on Youtube" />
                <HiddenLinkLabel>Watch us on Youtube</HiddenLinkLabel>
              </Link>
            </li>
          </SocialList>
        </Box>
      </Flex>
      <Flex flexWrap="wrap">
        <Box width={[null, 9 / 12]} pr={[null, 4]} mb={[4, 0]}>
          <Small as="p">
            Citygate Automotive Limited is an appointed representative of{' '}
            <InlineLink href="https://www.itccompliance.co.uk/welcome/">
              ITC Compliance Limited
            </InlineLink>{' '}
            which is authorised and regulated by the Financial Conduct Authority
            (their registration number is 313486). Permitted activities include
            advising on and arranging general insurance contracts and acting as
            a credit broker not a lender.
          </Small>
          <Small as="p">
            We can introduce you to a limited number of finance providers. We do
            not charge a fee for our Consumer Credit services. We do not act as
            a financial adviser, or fiduciary. We act in our own interest,
            whichever lender we introduce you to, we will typically receive
            commission from them based on either a fixed fee or a fixed
            percentage of the amount you borrow. Any and all commission amounts
            will be fully disclosed to you as part of your sales journey. You
            will be required to give your fully informed consent to our receipt
            of this commission. By doing this, you acknowledge that you
            understand our role as a credit broker, and that we will receive a
            financial incentive if you take out a loan from a lender that we
            introduce you to.
          </Small>
          <Small as="p">
            All finance applications are subject to status, terms and conditions
            apply, UK residents only, 18’s or over, Guarantees may be required.
            Reg. Office: 664 Victoria Road, Ruislip, Middlesex HA4&nbsp;0LN :
            Reg. Company No. 05615547 VAT No. 872&nbsp;286&nbsp;890
          </Small>
        </Box>
      </Flex>
      <Border
        flexWrap="wrap"
        alignItems="center"
        pt={2}
        pb={['30px', null]}
        mt={3}
      >
        <SiteWideLinks width={[12 / 12, 9 / 12]} pr={[null, 4]} mb={[4, 0]}>
          <Link pr={[1]} href="/legal">
            legal
          </Link>{' '}
          |{' '}
          <Link pr={[1]} href="/privacy-notice">
            privacy notice
          </Link>{' '}
          |{' '}
          <Link pr={[1]} href="/cookie-notice">
            cookie notice
          </Link>{' '}
          |{' '}
          <Link pr={[1]} href="/gender-pay-gap-report">
            gender&nbsp;pay&nbsp;gap
          </Link>{' '}
          |{' '}
          <Link pr={[1]} href="/slavery-and-human-trafficking">
            slavery&nbsp;and&nbsp;human&nbsp;trafficking
          </Link>{' '}
          |{' '}
          <Link px={[1]} href="/sitemap">
            sitemap
          </Link>{' '}
          |{' '}
          <Link pl={[1]} href={`${process.env.GATSBY_PORTAL_URL}/favourites`}>
            my&nbsp;favourites
          </Link>
        </SiteWideLinks>
        <SmallToMediumScreenOnly>
          <SmallImage src={assetLogoCitygate} alt="Citygate" />
        </SmallToMediumScreenOnly>
        <AutoWorks ml="auto" mb={[-1, 0]}>
          Powered by{' '}
          <Link href="https://auto.works" target="_blank" rel="noopener">
            auto.works
          </Link>
          <Link href="https://auto.works" ml={2}>
            <LogoAutoWorks alt="Autoworks logo" />
            <HiddenLinkLabel>Autoworks</HiddenLinkLabel>
          </Link>
        </AutoWorks>
      </Border>
    </Container>
  </FullWidthWrapper>
)

export default FooterCompany
